'use client';

import type { ComponentProps, ReactNode } from 'react';
import {
  Chat,
  ChatAttachmentPreviewsProvider,
  ChatEndpointsProvider,
  ChatHarProvider,
  ChatSessionExportProvider,
  ChatHttpTrackingProvider,
  ChatProxyHistoryProvider,
  BackgroundExecutionContextProvider,
  ChatBranchConfirmationDialog,
  type ChatRef,
} from '@terra/ui-design-system';
import { Box, HStack, css } from '@terra/ui-styled-system';
import type { MentionsMenuComposerRef } from '.././mentions/MentionsMenuComposer';
import {
  ChatHistoryLoadState,
  type ChatHistoryLoadStatus,
} from './ChatHistoryLoadState';
import { FeedbackProvider } from './feedback/FeedbackProvider';
import { CoPilotInitialState } from './CoPilotInitialState';
import { ComposerAttachMenu } from './ComposerAttachMenu';
import { ModeSelector } from './ModeSelector';
import { TerminalApprovalSelector } from './TerminalApprovalSelector';
import { CopilotPinnedPlanHeader } from './CopilotPinnedPlanHeader';
import {
  ChatSavePreferenceTrigger,
  ChatSavePreferencePanel,
  type useChatSavePreference,
} from './ChatSavePreferencePanel';
import { ContextUsageRing } from './token-usage/ContextUsageRing';
import { ChatFilesystemViewer } from './filesystem/ChatFilesystemViewer';
import { CoPilotDevToolsHub } from './dev-tools/CoPilotDevToolsHub';
import { ChatAgentMessagesViewer } from './dev-tools/agent-messages/ChatAgentMessagesViewer';
import type { TokenUsageEstimate } from './token-usage/tokenUsageEstimate';
import type { useCoPilotDevTools } from './dev-tools/useCoPilotDevTools';
import type { useCopilotMainContentNavigation } from './useCopilotMainContentNavigation';
import type { useCopilotChatProviders } from './useCopilotChatProviders';
import type { CopilotMode, TerraUIMessage } from '@terra/shared-dto-chat-types';
import { useCopilotSlashCommandsAndStatusLine } from './useCopilotSlashCommandsAndStatusLine';

const COPILOT_USER = { name: 'Terra User', role: 'Security Tester' };

const coPilotMarkdownTableStyles = css({
  '& .wmde-markdown th': {
    whiteSpace: 'nowrap',
  },
  '& .wmde-markdown td': {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    verticalAlign: 'top',
  },
});

type BaseChatProps = ComponentProps<typeof Chat>;
type BranchDialogProps = ComponentProps<typeof ChatBranchConfirmationDialog>;

export type CopilotChatInstanceViewProps = {
  tabId: string;
  chatId: string;
  isActive: boolean;
  appTargetId: string | undefined;
  targetId: string | undefined;
  showEntryAnimation: boolean;
  onClearEntryAnimation: () => void;
  handleChatDragOver: (event: React.DragEvent) => void;
  handleChatDrop: (event: React.DragEvent) => Promise<void>;
  handleChatPaste: (event: React.ClipboardEvent) => void;
  historyLoadStatus: ChatHistoryLoadStatus | null;
  historyLoadError: Error | null;
  retryHistoryLoad: () => void;
  showChat: boolean;
  chatComponentRef: React.RefObject<ChatRef | null>;
  messages: TerraUIMessage[];
  isLoading: boolean;
  streamIsLoading: boolean;
  hasActiveNeoAttackTracking: boolean;
  neoAttackTrackingIdentity: string;
  generatingIndicatorLoading: boolean;
  isReconnecting: boolean;
  isCompacting: boolean;
  compactionSummaryText: string | null;
  suppressPendingHITLLoading: boolean;
  messagePlaceholder: string;
  handleSendWithAttachments: BaseChatProps['onSend'];
  onEditUserMessage: BaseChatProps['onEditUserMessage'];
  onEditAskUserAnswer: BaseChatProps['onEditAskUserAnswer'];
  inlineEdit: BaseChatProps['inlineEdit'];
  checkpoint: BaseChatProps['checkpoint'];
  handleComposerInputChange: (value: string) => void;
  agentDefinition: NonNullable<BaseChatProps['agent']>;
  handleDataMessageEvent: BaseChatProps['onDataMessageEvent'];
  handleHITLDataMessageEvent: BaseChatProps['onHITLActionMessageEvent'];
  handleRetry: () => void;
  handleAbort: () => Promise<void>;
  handleAbortSubagent: BaseChatProps['onAbortSubagent'];
  isSubmitDisabled: boolean;
  allowEmptySubmit: boolean;
  mentionsMenuRef?: React.RefObject<MentionsMenuComposerRef | null>;
  subscribe: BaseChatProps['onSSESubscribe'];
  shouldShowFeedback: boolean;
  navigation: ReturnType<typeof useCopilotMainContentNavigation>;
  currentChatMode: CopilotMode;
  storedChatMode: CopilotMode | undefined;
  executePlan: () => void;
  handleRevertCopilotModeSwitch: BaseChatProps['onRevertCopilotModeSwitch'];
  hasLatestPlan: boolean;
  handleAlwaysAllowRule: BaseChatProps['onAlwaysAllowRule'];
  skillDraftReference: BaseChatProps['skillDraftReference'];
  loadMoreMessages: BaseChatProps['onLoadMore'];
  hasMoreMessages: boolean;
  isLoadingMoreMessages: boolean;
  composerTopContent: ReactNode | undefined;
  attachmentsPanel: ReactNode;
  isNewChat: boolean;
  handlePromptSuggestionClick: (promptText: string) => void;
  savePreference: ReturnType<typeof useChatSavePreference>;
  devToolsEnabled: boolean;
  activeTool: ReturnType<typeof useCoPilotDevTools>['activeTool'];
  toggleTool: ReturnType<typeof useCoPilotDevTools>['toggleTool'];
  closeTool: ReturnType<typeof useCoPilotDevTools>['closeTool'];
  isTokenUsageOpen: boolean;
  isFilesystemOpen: boolean;
  isAgentMessagesOpen: boolean;
  tokenUsageEstimate: TokenUsageEstimate;
  workingCount: number;
  uploadFiles: (files: File[]) => Promise<void> | void;
  isBranchConfirmOpen: boolean;
  isApplyingBranch: boolean;
  branchConfirmVariant: BranchDialogProps['variant'];
  confirmBranchResend: BranchDialogProps['onConfirm'];
  cancelBranchResend: BranchDialogProps['onCancel'];
  handleBranchConfirmOpenChange: BranchDialogProps['onOpenChange'];
  fileUploadMenu: ReactNode;
  isStreamingStatus: boolean;
  providers: ReturnType<typeof useCopilotChatProviders>;
  imagePreviewUrls: Record<string, string>;
};

function CopilotChatSurface(props: CopilotChatInstanceViewProps) {
  const {
    tabId,
    chatId,
    isActive,
    appTargetId,
    targetId,
    showEntryAnimation,
    onClearEntryAnimation,
    handleChatDragOver,
    handleChatDrop,
    handleChatPaste,
    historyLoadStatus,
    historyLoadError,
    retryHistoryLoad,
    showChat,
    chatComponentRef,
    messages,
    isLoading,
    streamIsLoading,
    hasActiveNeoAttackTracking,
    neoAttackTrackingIdentity,
    generatingIndicatorLoading,
    isReconnecting,
    isCompacting,
    compactionSummaryText,
    suppressPendingHITLLoading,
    messagePlaceholder,
    handleSendWithAttachments,
    onEditUserMessage,
    onEditAskUserAnswer,
    inlineEdit,
    checkpoint,
    handleComposerInputChange,
    agentDefinition,
    handleDataMessageEvent,
    handleHITLDataMessageEvent,
    handleRetry,
    handleAbort,
    handleAbortSubagent,
    isSubmitDisabled,
    allowEmptySubmit,
    mentionsMenuRef,
    subscribe,
    shouldShowFeedback,
    navigation,
    currentChatMode,
    storedChatMode,
    executePlan,
    handleRevertCopilotModeSwitch,
    hasLatestPlan,
    handleAlwaysAllowRule,
    skillDraftReference,
    loadMoreMessages,
    hasMoreMessages,
    isLoadingMoreMessages,
    composerTopContent,
    attachmentsPanel,
    isNewChat,
    handlePromptSuggestionClick,
    savePreference,
    devToolsEnabled,
    activeTool,
    toggleTool,
    closeTool,
    isTokenUsageOpen,
    isFilesystemOpen,
    isAgentMessagesOpen,
    tokenUsageEstimate,
    workingCount,
    uploadFiles,
    isBranchConfirmOpen,
    isApplyingBranch,
    branchConfirmVariant,
    confirmBranchResend,
    cancelBranchResend,
    handleBranchConfirmOpenChange,
    fileUploadMenu,
    isStreamingStatus,
  } = props;

  const { slashCommands, handleCycleMode, statusLineContent } =
    useCopilotSlashCommandsAndStatusLine({
      chatId,
      appTargetId,
      targetId,
      messages,
      tokenUsageEstimate,
      workingCount,
      handleSend: handleSendWithAttachments,
      onToggleTokenUsage: devToolsEnabled
        ? () => toggleTool('token-usage')
        : undefined,
    });

  return (
    <Box
      className={coPilotMarkdownTableStyles}
      display={isActive ? 'flex' : 'none'}
      flexDirection="column"
      h="full"
      w="full"
      position="relative"
      data-copilot-chat-surface=""
      animation={isActive && showEntryAnimation ? 'fade-in' : undefined}
      animationDuration="0.15s"
      onAnimationEnd={onClearEntryAnimation}
      onDragOver={handleChatDragOver}
      onDrop={handleChatDrop}
      onPaste={handleChatPaste}
    >
      <ChatHistoryLoadState
        status={historyLoadStatus}
        historyLoadError={historyLoadError}
        onRetry={retryHistoryLoad}
      />
      {showChat ? (
        <Chat
          key={chatId}
          ref={chatComponentRef}
          instanceId={tabId}
          isActive={isActive}
          messageHistorySessionId={chatId}
          messages={messages}
          isLoading={isLoading}
          streamIsLoading={streamIsLoading}
          hasActiveNeoAttackTracking={hasActiveNeoAttackTracking}
          neoAttackTrackingIdentity={neoAttackTrackingIdentity}
          generatingIndicatorLoading={generatingIndicatorLoading}
          isReconnecting={isReconnecting}
          isCompacting={isCompacting}
          compactionSummaryText={compactionSummaryText}
          suppressPendingHITLLoading={suppressPendingHITLLoading}
          composerVariant="copilot"
          messagePlaceholder={messagePlaceholder}
          sendButtonText="Send"
          onSend={handleSendWithAttachments}
          onCycleMode={handleCycleMode}
          slashCommands={slashCommands}
          statusLineContent={isActive ? statusLineContent : undefined}
          onEditUserMessage={onEditUserMessage}
          onEditAskUserAnswer={onEditAskUserAnswer}
          inlineEdit={inlineEdit}
          checkpoint={checkpoint}
          onInputChange={handleComposerInputChange}
          agent={agentDefinition}
          user={COPILOT_USER}
          onDataMessageEvent={handleDataMessageEvent}
          onHITLActionMessageEvent={handleHITLDataMessageEvent}
          onRetry={handleRetry}
          onAbort={handleAbort}
          onAbortSubagent={handleAbortSubagent}
          isSubmitDisabled={isSubmitDisabled}
          allowEmptySubmit={allowEmptySubmit}
          onMentionsTrigger={
            isActive
              ? (position) => {
                  mentionsMenuRef?.current?.open(position);
                }
              : undefined
          }
          onSSESubscribe={subscribe}
          noAssistantHeader={messages.length > 1}
          showFileUpload={false}
          isFeedbackEnabled={shouldShowFeedback}
          onOpenEditInMainContent={navigation.handleOpenEditInMainContent}
          onCloseEditInMainContent={navigation.handleCloseEditInMainContent}
          onOpenHttpInMainContent={navigation.handleOpenHttpInMainContent}
          onOpenNodeArgsInMainContent={
            navigation.handleOpenNodeArgsInMainContent
          }
          onOpenBrowserStreamInMainContent={
            navigation.handleOpenBrowserStreamInMainContent
          }
          onOpenPlanInMainContent={navigation.handleOpenPlanInMainContent}
          onBuildPlan={currentChatMode === 'agent' ? undefined : executePlan}
          onRevertCopilotModeSwitch={handleRevertCopilotModeSwitch}
          onConfirmCopilotModeSwitch={handleRevertCopilotModeSwitch}
          currentCopilotMode={storedChatMode}
          pinnedHeaderContent={
            hasLatestPlan && currentChatMode === 'agent' ? (
              <CopilotPinnedPlanHeader chatId={chatId} messages={messages} />
            ) : undefined
          }
          hideInlinePlanAndTodos={hasLatestPlan && currentChatMode === 'agent'}
          onOpenObject={navigation.handleOpenChatObject}
          onAlwaysAllowRule={handleAlwaysAllowRule}
          skillDraftReference={skillDraftReference}
          onLoadMore={loadMoreMessages}
          hasMoreMessages={hasMoreMessages}
          isLoadingMoreMessages={isLoadingMoreMessages}
          composerTopContent={composerTopContent}
          composerAttachments={attachmentsPanel}
          emptyState={
            isNewChat ? (
              <CoPilotInitialState
                onPromptClick={handlePromptSuggestionClick}
              />
            ) : undefined
          }
          footerLeftContent={
            isActive ? (
              <HStack
                gap={1}
                alignItems="center"
                flexWrap="wrap"
                minW={0}
                w="full"
              >
                <ModeSelector chatId={chatId} />
                <TerminalApprovalSelector />
                <ChatSavePreferenceTrigger
                  isLoading={isLoading}
                  onOpen={savePreference.open}
                />
                {devToolsEnabled && (
                  <CoPilotDevToolsHub
                    activeTool={activeTool}
                    onToggleTool={toggleTool}
                  />
                )}
              </HStack>
            ) : undefined
          }
          footerRightContent={
            isActive ? (
              <HStack gap={1} alignItems="center" flexShrink={0}>
                {devToolsEnabled && (
                  <ContextUsageRing
                    estimate={tokenUsageEstimate}
                    isOpen={isTokenUsageOpen}
                    onToggle={() => toggleTool('token-usage')}
                  />
                )}
                <ComposerAttachMenu
                  isLoading={isLoading}
                  mentionsMenuRef={mentionsMenuRef}
                  onFilesSelected={(files) => {
                    void uploadFiles(files);
                  }}
                />
              </HStack>
            ) : undefined
          }
        />
      ) : null}

      <ChatBranchConfirmationDialog
        isOpen={isBranchConfirmOpen}
        isLoading={isApplyingBranch}
        variant={branchConfirmVariant}
        onConfirm={confirmBranchResend}
        onCancel={cancelBranchResend}
        onOpenChange={handleBranchConfirmOpenChange}
      />

      {fileUploadMenu}

      <ChatSavePreferencePanel
        isOpen={savePreference.isOpen}
        isSaving={savePreference.isSaving}
        content={savePreference.content}
        onContentChange={savePreference.setContent}
        onClose={savePreference.close}
        onSave={savePreference.handleSave}
      />

      {devToolsEnabled && (
        <>
          <ChatFilesystemViewer
            chatId={chatId}
            applicationId={appTargetId}
            isOpen={isFilesystemOpen}
            onClose={() => closeTool('filesystem')}
          />
          <ChatAgentMessagesViewer
            chatId={chatId}
            targetId={targetId}
            isOpen={isAgentMessagesOpen}
            isStreaming={isStreamingStatus}
            onClose={() => closeTool('agent-messages')}
          />
        </>
      )}
    </Box>
  );
}

export function CopilotChatInstanceView(props: CopilotChatInstanceViewProps) {
  const { chatId, providers, imagePreviewUrls } = props;

  return (
    <BackgroundExecutionContextProvider
      getBackgroundExecutionByToolCallId={
        providers.getBackgroundExecutionByToolCallId
      }
    >
      <ChatEndpointsProvider fetchEndpoints={providers.handleFetchEndpoints}>
        <ChatHarProvider
          threadId={chatId}
          fetchHarData={providers.handleFetchHarData}
          fetchHarById={providers.fetchHarById}
          hasHarPresence={providers.hasHarPresence}
        >
          <ChatSessionExportProvider
            downloadSessionExport={providers.downloadSessionExport}
          >
            <ChatProxyHistoryProvider
              fetchHistoryRequestForHitlDisplay={
                providers.handleFetchHistoryRequestForHitlDisplay
              }
            >
              <ChatHttpTrackingProvider
                onTrackRequest={providers.handleTrackHttpRequest}
              >
                <ChatAttachmentPreviewsProvider
                  imagePreviewUrls={imagePreviewUrls}
                >
                  <FeedbackProvider isEnabled chatId={chatId}>
                    <CopilotChatSurface {...props} />
                  </FeedbackProvider>
                </ChatAttachmentPreviewsProvider>
              </ChatHttpTrackingProvider>
            </ChatProxyHistoryProvider>
          </ChatSessionExportProvider>
        </ChatHarProvider>
      </ChatEndpointsProvider>
    </BackgroundExecutionContextProvider>
  );
}
